import React, { useEffect, useRef } from 'react';
import { useI18n } from '../i18n';

interface GeoPos {
  latitude: number;
  longitude: number;
}

interface PoiNavigationOverlayProps {
  isNavigating: boolean;
  poiName: string | null;
  /** Path network segments — same format as PathNetworkMiniMap */
  pathNetwork?: number[][][];
  /** Where the camera was when navigation started */
  fromPosition?: GeoPos;
  /** The destination POI's position */
  toPosition?: GeoPos;
}

const W = 240;
const H = 140;
/** How long one from→to sweep takes, in ms — slow and deliberate */
const SWEEP_DURATION = 12000;

/**
 * Draws one frame of the minimap journey.
 * Returns the rAF id so the caller can cancel it.
 */
function drawFrame(
  ctx: CanvasRenderingContext2D,
  pathNetwork: number[][][],
  from: GeoPos,
  to: GeoPos,
  progress: number, // 0→1, eased
): void {
  ctx.clearRect(0, 0, W, H);

  // ── Bounds (include both endpoints so they're never clipped) ──────────────
  let minLon = Math.min(from.longitude, to.longitude);
  let maxLon = Math.max(from.longitude, to.longitude);
  let minLat = Math.min(from.latitude,  to.latitude);
  let maxLat = Math.max(from.latitude,  to.latitude);

  pathNetwork.forEach(seg => seg.forEach(([lon, lat]) => {
    minLon = Math.min(minLon, lon); maxLon = Math.max(maxLon, lon);
    minLat = Math.min(minLat, lat); maxLat = Math.max(maxLat, lat);
  }));

  const pad = 18;
  const lonRange = maxLon - minLon || 0.001;
  const latRange = maxLat - minLat || 0.001;

  const sx = (lon: number) => pad + ((lon - minLon) / lonRange) * (W - pad * 2);
  const sy = (lat: number) => H - pad - ((lat - minLat) / latRange) * (H - pad * 2);

  // ── Path network — faint ──────────────────────────────────────────────────
  ctx.strokeStyle = 'rgba(107,114,128,0.45)';
  ctx.lineWidth = 1.2;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  pathNetwork.forEach(seg => {
    if (seg.length < 2) return;
    ctx.beginPath();
    ctx.moveTo(sx(seg[0][0]), sy(seg[0][1]));
    for (let i = 1; i < seg.length; i++) ctx.lineTo(sx(seg[i][0]), sy(seg[i][1]));
    ctx.stroke();
  });

  // ── Dashed line from→to ───────────────────────────────────────────────────
  const fx = sx(from.longitude), fy = sy(from.latitude);
  const tx = sx(to.longitude),   ty = sy(to.latitude);

  ctx.beginPath();
  ctx.setLineDash([4, 4]);
  ctx.strokeStyle = 'white';
  ctx.lineWidth = 1.5;
  ctx.moveTo(fx, fy);
  ctx.lineTo(tx, ty);
  ctx.stroke();
  ctx.setLineDash([]);

  // ── Destination pin ───────────────────────────────────────────────────────
  const pinR = 5;
  const pinTipY = ty + pinR * 1.8;
  ctx.beginPath();
  ctx.arc(tx, ty, pinR, Math.PI * 0.15, Math.PI * 0.85, true);
  ctx.lineTo(tx, pinTipY);
  ctx.closePath();
  ctx.fillStyle = '#0CB4E4';
  ctx.fill();
  ctx.strokeStyle = 'rgba(255,255,255,0.7)';
  ctx.lineWidth = 1;
  ctx.stroke();
  // inner dot
  ctx.beginPath();
  ctx.arc(tx, ty - pinR * 0.1, pinR * 0.32, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(255,255,255,0.9)';
  ctx.fill();

  // ── Animated traveller dot ────────────────────────────────────────────────
  const dotX = fx + (tx - fx) * progress;
  const dotY = fy + (ty - fy) * progress;

  // Glow halo
  const grd = ctx.createRadialGradient(dotX, dotY, 0, dotX, dotY, 10);
  grd.addColorStop(0,   'rgba(253,186,89,0.35)');
  grd.addColorStop(1,   'rgba(253,186,89,0)');
  ctx.beginPath();
  ctx.arc(dotX, dotY, 10, 0, Math.PI * 2);
  ctx.fillStyle = grd;
  ctx.fill();

  // Core dot
  ctx.beginPath();
  ctx.arc(dotX, dotY, 4, 0, Math.PI * 2);
  ctx.fillStyle = '#EF4444';
  ctx.fill();
  ctx.strokeStyle = '#fff';
  ctx.lineWidth = 1.5;
  ctx.stroke();
}

/** Ease-in-out cubic */
function easeInOut(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

// ─── Component ────────────────────────────────────────────────────────────────

const PoiNavigationOverlay: React.FC<PoiNavigationOverlayProps> = ({
  isNavigating,
  poiName,
  pathNetwork,
  fromPosition,
  toPosition,
}) => {
  const { t } = useI18n();
  const canvasRef     = useRef<HTMLCanvasElement>(null);
  const rafRef        = useRef<number>(0);
  const startRef      = useRef<number>(0);
  // Snapshot from/to at the moment navigation starts — never updated mid-flight
  // so re-renders during navigation (camera polling, segment changes) can't reset the clock
  const frozenFromRef = useRef<GeoPos | null>(null);
  const frozenToRef   = useRef<GeoPos | null>(null);

  const hasMap = !!(pathNetwork?.length && fromPosition && toPosition);

  // Capture positions once when navigation begins, clear when it ends
  useEffect(() => {
    if (isNavigating && fromPosition && toPosition) {
      frozenFromRef.current = fromPosition;
      frozenToRef.current   = toPosition;
    } else if (!isNavigating) {
      frozenFromRef.current = null;
      frozenToRef.current   = null;
    }
  }, [isNavigating]); // eslint-disable-line react-hooks/exhaustive-deps

  // Animation loop — only depends on isNavigating + hasMap, not on object identity
  useEffect(() => {
    if (!isNavigating || !hasMap) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    startRef.current = performance.now();

    const tick = (now: number) => {
      const from = frozenFromRef.current;
      const to   = frozenToRef.current;
      if (!from || !to || !pathNetwork) return;

      const elapsed  = now - startRef.current;
      const raw      = Math.min(elapsed / SWEEP_DURATION, 1);
      const progress = easeInOut(raw);

      drawFrame(ctx, pathNetwork, from, to, progress);
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [isNavigating, hasMap]); // ← no fromPosition/toPosition — frozen refs are stable

  if (!isNavigating) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-[2000] flex items-center justify-center"
        style={{ pointerEvents: 'all', animation: 'nav-fadein 0.25s ease forwards' }}
      >
        {/* Blurred backdrop */}
        <div
          className="absolute inset-0"
          style={{
            background: 'rgba(0,0,0,0.5)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
          }}
        />

        {/* Card */}
        <div
          className="relative flex flex-col items-center gap-3 px-6 py-5 rounded-2xl"
          style={{
            background: 'rgba(0,0,0,0.6)',
            
            boxShadow: '0 8px 32px rgba(0,0,0,0.45)',
            backdropFilter: 'blur(16px)',
            width: 280,
            animation: 'nav-slide-up 0.25s ease forwards',
          }}
        >
          {/* Label row */}
          <div style={{ textAlign: 'center' }}>
            <div style={{
              fontSize: 10, color: 'white',
              textTransform: 'uppercase', letterSpacing: '0.14em', marginBottom: 4,
            }}>
              {t.poiNav.navigatingTo}
            </div>
            <div style={{
              fontSize: 15, fontWeight: 700, color: 'white', fontFamily: 'Arial',
            }}>
              {poiName}
            </div>
          </div>

          {/* Minimap journey — shown when path data available */}
          {hasMap ? (
            <canvas
              ref={canvasRef}
              width={W}
              height={H}
              style={{
                width: '100%',
                borderRadius: 10,
                background: 'rgba(0,0,0,0.35)',
                border: '1px solid rgba(255,255,255,0.07)',
              }}
            />
          ) : (
            /* Fallback: animated pin when no map data */
            <div style={{ position: 'relative', width: 36, height: 36 }}>
              <svg width="36" height="36" viewBox="0 0 28 38" fill="none">
                <defs>
                  <radialGradient id="navPinGrad" cx="38%" cy="35%" r="60%">
                    <stop offset="0%" stopColor="#FFD580" />
                    <stop offset="100%" stopColor="#F59E0B" />
                  </radialGradient>
                </defs>
                <path
                  d="M14 2C8.477 2 4 6.477 4 12c0 7.732 10 24 10 24s10-16.268 10-24c0-5.523-4.477-10-10-10z"
                  fill="url(#navPinGrad)" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5"
                />
                <circle cx="14" cy="12" r="4" fill="white" fillOpacity="0.9" />
              </svg>
              <div style={{
                position: 'absolute', top: '50%', left: '50%',
                width: 48, height: 48, borderRadius: '50%',
                border: '2px solid rgba(253,186,89,0.55)',
                animation: 'nav-pulse 1.4s ease-out infinite',
                pointerEvents: 'none',
              }} />
            </div>
          )}

          {/* Status line */}
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.28)', fontFamily: 'Arial' }}>
            {t.poiNav.loadingScene}
          </div>

          {/* Progress bar */}
          <div style={{
            width: '100%', height: 2,
            background: 'rgba(255,255,255,0.07)', borderRadius: 2, overflow: 'hidden',
          }}>
            <div style={{
              height: '100%',
              background: 'linear-gradient(90deg, transparent, #0CB4E4, #F59E0B, transparent)',
              animation: 'nav-progress 1.6s ease-in-out infinite',
            }} />
          </div>
        </div>
      </div>

      <style>{`
        @keyframes nav-fadein   { from { opacity:0 } to { opacity:1 } }
        @keyframes nav-slide-up {
          from { opacity:0; transform:translateY(10px) }
          to   { opacity:1; transform:translateY(0)    }
        }
        @keyframes nav-pulse {
          0%   { transform:translate(-50%,-50%) scale(0.5); opacity:0.9 }
          100% { transform:translate(-50%,-50%) scale(2.2); opacity:0   }
        }
        @keyframes nav-progress {
          0%   { transform:translateX(-100%); width:60% }
          100% { transform:translateX(280px); width:60% }
        }
      `}</style>
    </>
  );
};

export default PoiNavigationOverlay;